<script setup>
import { ref, computed, getCurrentInstance, onMounted, onUnmounted } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption
} from '@headlessui/vue';

const { proxy } = getCurrentInstance();
const { $stores, $constants, $bus } = proxy;

const router = useRouter();
const loading = computed(() => $stores?.user?.state?.loading || false);
const showPassword = ref(false);
const errorMessage = ref('');

const countries = [
  { code: '229', name: 'Bénin', flag: 'https://flagcdn.com/w40/bj.png' },
  { code: '228', name: 'Togo', flag: 'https://flagcdn.com/w40/tg.png' },
  { code: '225', name: "Côte d'Ivoire", flag: 'https://flagcdn.com/w40/ci.png' },
  { code: '226', name: 'Burkina Faso', flag: 'https://flagcdn.com/w40/bf.png' },
  { code: '223', name: 'Mali', flag: 'https://flagcdn.com/w40/ml.png' },
  { code: '221', name: 'Sénégal', flag: 'https://flagcdn.com/w40/sn.png' },
  { code: '241', name: 'Gabon', flag: 'https://flagcdn.com/w40/ga.png' },
  { code: '227', name: 'Niger', flag: 'https://flagcdn.com/w40/ne.png' }
];

const selectedCountry = ref(countries[0]);

const onRegisterSuccess = () => {
  router.push('/dashboard');
};

const onRegisterError = (error) => {
  errorMessage.value = error?.data?.message || 'Échec de l\'inscription. Veuillez réessayer.';
};

const handleRegister = () => {
  errorMessage.value = '';
  const form = document.getElementById('registerForm');
  const formData = new FormData(form);

  const data = {
    firstName: formData.get('firstName'),
    lastName: formData.get('lastName'),
    email: formData.get('email'),
    password: formData.get('password'),
    phonePrefix: formData.get('phonePrefix'),
    phoneNumber: formData.get('phoneNumber'),
    termOfUse: formData.get('termOfUse') === 'on',
    acceptMoreThan18: true,
  };

  if (!data.firstName || !data.lastName || !data.email || !data.password || !data.phoneNumber) {
    errorMessage.value = 'Veuillez remplir tous les champs obligatoires';
    return;
  }

  if (!data.termOfUse) {
    errorMessage.value = 'Veuillez accepter les conditions d\'utilisation';
    return;
  }

  proxy.$bus?.emit($constants?.EVENTS.USER.REGISTER, data);
};

onMounted(() => {
  $bus?.on($constants?.EVENTS.USER.REGISTER_SUCCESS, onRegisterSuccess);
  $bus?.on($constants?.EVENTS.USER.REGISTER_ERROR, onRegisterError);
});

onUnmounted(() => {
  $bus?.off($constants?.EVENTS.USER.REGISTER_SUCCESS, onRegisterSuccess);
  $bus?.off($constants?.EVENTS.USER.REGISTER_ERROR, onRegisterError);
});
</script>

<template>
  <div class="min-h-screen flex" style="background: #0A0A0B;">
    <!-- Left Side - Branding -->
    <div class="hidden lg:flex lg:w-1/2 relative overflow-hidden">
      <div class="absolute inset-0 bg-gradient-to-br from-[#4b4ced]/20 to-[#37b6e9]/20"></div>
      <div class="absolute inset-0" style="background: radial-gradient(circle at 30% 50%, rgba(75, 76, 237, 0.1) 0%, transparent 50%), radial-gradient(circle at 70% 50%, rgba(55, 182, 233, 0.1) 0%, transparent 50%);"></div>

      <div class="relative z-10 flex flex-col justify-between h-full p-12 text-white">
        <div>
          <RouterLink to="/" class="flex items-center gap-3">
            <div class="w-12 h-12 rounded-xl flex items-center justify-center" style="background: linear-gradient(135deg, #37b6e9 0%, #2e9ac4 100%); box-shadow: 0 4px 15px rgba(55, 182, 233, 0.3);">
              <span class="material-icons-outlined text-white text-xl">store</span>
            </div>
            <div class="flex flex-col">
              <span class="font-bold text-xl" style="font-family: 'Outfit', sans-serif;">Kingrecharges</span>
              <span class="text-xs text-white/50">Premium Store</span>
            </div>
          </RouterLink>
        </div>

        <div class="max-w-lg">
          <h1 class="text-5xl font-black mb-6 leading-tight" style="font-family: 'Outfit', sans-serif;">
            Rejoignez la communauté Kingrecharges
          </h1>
          <p class="text-white/70 text-lg leading-relaxed">
            Créez votre compte en quelques secondes et accédez aux meilleurs abonnements numériques à des prix imbattables.
          </p>
        </div>

        <div class="space-y-4">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg flex items-center justify-center" style="background: rgba(16, 185, 129, 0.15);">
              <span class="material-icons-outlined" style="color: #10B981;">check</span>
            </div>
            <span class="text-white/80">Livraison instantanée par email</span>
          </div>
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg flex items-center justify-center" style="background: rgba(16, 185, 129, 0.15);">
              <span class="material-icons-outlined" style="color: #10B981;">check</span>
            </div>
            <span class="text-white/80">Support client disponible 24/7</span>
          </div>
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg flex items-center justify-center" style="background: rgba(16, 185, 129, 0.15);">
              <span class="material-icons-outlined" style="color: #10B981;">check</span>
            </div>
            <span class="text-white/80">Prix les plus bas du marché</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Side - Registration Form -->
    <div class="flex-1 flex items-center justify-center p-6 md:p-12 overflow-y-auto">
      <div class="w-full max-w-md">
        <!-- Mobile Logo -->
        <div class="lg:hidden text-center mb-8">
          <RouterLink to="/" class="inline-flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl flex items-center justify-center" style="background: linear-gradient(135deg, #37b6e9 0%, #2e9ac4 100%);">
              <span class="material-icons-outlined text-white">store</span>
            </div>
            <span class="font-bold text-lg text-white" style="font-family: 'Outfit', sans-serif;">Kingrecharges</span>
          </RouterLink>
        </div>

        <div class="mb-8">
          <h1 class="text-3xl font-black text-white mb-2" style="font-family: 'Outfit', sans-serif;">Inscription</h1>
          <p class="text-white/60">Créez votre compte pour commencer</p>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="mb-6 p-4 rounded-xl" style="background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.2);">
          <p class="text-sm text-red-400 flex items-center gap-2">
            <span class="material-icons-outlined text-lg">error</span>
            <span>{{ errorMessage }}</span>
          </p>
        </div>

        <form id="registerForm" @submit.prevent="handleRegister" class="space-y-4">
          <!-- Nom et Prénom -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-semibold mb-2 text-white/70">Prénom</label>
              <input
                name="firstName"
                type="text"
                required
                class="w-full px-4 py-3.5 rounded-xl text-white transition-all"
                style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.06);"
                placeholder="Jean"
              />
            </div>
            <div>
              <label class="block text-sm font-semibold mb-2 text-white/70">Nom</label>
              <input
                name="lastName"
                type="text"
                required
                class="w-full px-4 py-3.5 rounded-xl text-white transition-all"
                style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.06);"
                placeholder="Dupont"
              />
            </div>
          </div>

          <!-- Téléphone -->
          <div>
            <label class="block text-sm font-semibold mb-2 text-white/70">Téléphone</label>
            <div class="flex gap-2">
              <Listbox v-model="selectedCountry" as="div">
                <ListboxButton class="px-3 py-3.5 rounded-xl flex items-center gap-2 transition-all" style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.06);">
                  <img v-if="selectedCountry" :src="selectedCountry.flag" class="w-5 h-4 object-cover rounded-sm" />
                  <span class="text-sm text-white">{{ selectedCountry?.code || '+' }}</span>
                  <span class="material-icons-outlined text-lg text-white/50">expand_more</span>
                </ListboxButton>
                <ListboxOptions class="absolute z-50 mt-1 max-h-60 w-auto overflow-auto rounded-xl py-1 shadow-xl" style="background: rgba(26, 26, 29, 0.95); border: 1px solid rgba(255, 255, 255, 0.1);">
                  <ListboxOption
                    v-for="country in countries"
                    :key="country.code"
                    :value="country"
                    class="relative cursor-pointer select-none py-2 px-3 hover:bg-white/5 flex items-center gap-2 whitespace-nowrap"
                    v-slot="{ selected }"
                  >
                    <img :src="country.flag" class="w-5 h-4 object-cover rounded-sm" />
                    <span class="font-medium text-white">+{{ country.code }}</span>
                    <span class="text-white/50 ml-1 text-sm">{{ country.name }}</span>
                  </ListboxOption>
                </ListboxOptions>
              </Listbox>
              <input
                name="phoneNumber"
                type="tel"
                required
                class="flex-1 px-4 py-3.5 rounded-xl text-white transition-all"
                style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.06);"
                placeholder="01 02 03 04 05"
              />
              <input
                name="phonePrefix"
                type="hidden"
                :value="selectedCountry?.code || ''"
              />
            </div>
          </div>

          <!-- Email -->
          <div>
            <label class="block text-sm font-semibold mb-2 text-white/70">Email</label>
            <div class="relative">
              <span class="absolute left-4 top-1/2 -translate-y-1/2 material-icons-outlined text-white/30">email</span>
              <input
                name="email"
                type="email"
                required
                class="w-full pl-12 pr-4 py-3.5 rounded-xl text-white transition-all"
                style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.06);"
                placeholder="votre@email.com"
              />
            </div>
          </div>

          <!-- Mot de passe -->
          <div>
            <label class="block text-sm font-semibold mb-2 text-white/70">Mot de passe</label>
            <div class="relative">
              <span class="absolute left-4 top-1/2 -translate-y-1/2 material-icons-outlined text-white/30">lock</span>
              <input
                name="password"
                :type="showPassword ? 'text' : 'password'"
                required
                class="w-full pl-12 pr-12 py-3.5 rounded-xl text-white transition-all"
                style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.06);"
                placeholder="Votre mot de passe"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
              >
                <span class="material-icons-outlined">{{ showPassword ? 'visibility_off' : 'visibility' }}</span>
              </button>
            </div>
          </div>

          <!-- Terms checkbox -->
          <label class="flex items-start gap-3 cursor-pointer">
            <input
              name="termOfUse"
              type="checkbox"
              required
              class="mt-1 w-5 h-5 rounded border-gray-300 transition-all"
              style="accent-color: #37b6e9;"
            />
            <span class="text-sm text-white/60">
              J'accepte les
              <a href="#" class="font-semibold hover:underline" style="color: #37b6e9;">conditions d'utilisation</a>
              et la
              <a href="#" class="font-semibold hover:underline" style="color: #37b6e9;">politique de confidentialité</a>
            </span>
          </label>

          <!-- Submit button -->
          <button
            type="submit"
            :disabled="loading"
            class="w-full py-4 rounded-xl font-bold text-white transition-all hover:scale-[1.02] hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
            style="background: linear-gradient(135deg, #37b6e9 0%, #2e9ac4 100%); box-shadow: 0 4px 20px rgba(55, 182, 233, 0.3);"
          >
            <span v-if="loading" class="material-icons-outlined animate-spin">refresh</span>
            <span v-else>Créer mon compte</span>
          </button>
        </form>

        <!-- Sign in link -->
        <p class="text-center text-white/60 mt-8">
          Déjà un compte ?
          <RouterLink to="/login" class="font-semibold hover:underline" style="color: #37b6e9;">Se connecter</RouterLink>
        </p>

        <!-- Back to home -->
        <div class="text-center mt-6">
          <RouterLink to="/" class="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors">
            <span class="material-icons-outlined text-lg">arrow_back</span>
            Retour à l'accueil
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>
